import { useEffect, useRef, useState } from "react";
import { Activity } from "lucide-react";
import { letterBlast, katanaWipe } from "@/lib/motion";

/**
 * First-visit splash screen. Each letter of "BET CRACKER" flies in from
 * below with a spring overshoot + neon glow ripple (anime.js v4
 * `letterBlast`), the badge pulses, then a diagonal katana slash
 * (`katanaWipe`) wipes the splash off-screen revealing the app.
 *
 * Shown at most once per browser tab session via ``sessionStorage``.
 */
export default function SplashScreen() {
  const [visible, setVisible] = useState(() => {
    try { return sessionStorage.getItem("bc_splashed") !== "1"; }
    catch { return true; }
  });
  const [fading, setFading] = useState(false);
  const betRef = useRef(null);
  const crackerRef = useRef(null);

  useEffect(() => {
    if (!visible) return;
    try { sessionStorage.setItem("bc_splashed", "1"); } catch { /* ignore */ }

    const cssAccent = getComputedStyle(document.documentElement)
      .getPropertyValue("--bc-accent").trim() || "#22C55E";
    const cssGlow = getComputedStyle(document.documentElement)
      .getPropertyValue("--bc-glow").trim() || "rgba(34,197,94,0.55)";

    // Two-stage letter blast: BET in white, CRACKER in accent — staggered
    if (betRef.current) {
      letterBlast(betRef.current, "BET", { color: "var(--bc-ink, #fff)", glow: "rgba(255,255,255,0.75)" });
    }
    if (crackerRef.current) {
      // Slight delay so "BET" finishes its main fall before "CRACKER" starts
      setTimeout(() => {
        letterBlast(crackerRef.current, " CRACKER", { color: cssAccent, glow: cssGlow });
      }, 350);
    }

    const wipeTimer = setTimeout(() => katanaWipe({ color: cssAccent, duration: 720 }), 1900);
    const fadeTimer = setTimeout(() => setFading(true), 2050);
    const hideTimer = setTimeout(() => setVisible(false), 2700);
    return () => {
      clearTimeout(wipeTimer); clearTimeout(fadeTimer); clearTimeout(hideTimer);
    };
  }, [visible]);

  if (!visible) return null;
  const cssAccent = getComputedStyle(document.documentElement)
    .getPropertyValue("--bc-accent").trim() || "#22C55E";
  const cssGlow = getComputedStyle(document.documentElement)
    .getPropertyValue("--bc-glow").trim() || "rgba(34,197,94,0.55)";

  return (
    <div
      data-testid="splash-screen"
      aria-hidden
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "var(--bc-bg, var(--bc-bg, #020202))",
        display: "flex", alignItems: "center", justifyContent: "center",
        pointerEvents: "none",
        opacity: fading ? 0 : 1,
        transition: "opacity 450ms ease-out",
      }}
    >
      <style>{`
        @keyframes bcBadgeIn {
          0%   { opacity: 0; transform: scale(0) rotate(-180deg); }
          70%  { opacity: 1; transform: scale(1.15) rotate(15deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes bcBadgePulse {
          0%, 100% { box-shadow: 0 0 24px var(--g, rgba(34,197,94,0.55)); }
          50%      { box-shadow: 0 0 60px 14px var(--g, rgba(34,197,94,0.55)); }
        }
        @keyframes bcCaretBlink {
          0%, 100% { opacity: 0; }
          30%, 70% { opacity: 1; }
        }
      `}</style>
      <div style={{
        position: "relative",
        display: "flex", alignItems: "center", gap: "1rem",
        padding: "1.5rem 2rem",
      }}>
        <div
          style={{
            width: 58, height: 58,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: cssAccent,
            clipPath: "polygon(12% 0%, 88% 0%, 100% 12%, 100% 88%, 88% 100%, 12% 100%, 0% 88%, 0% 12%)",
            ["--g"]: cssGlow,
            animation: "bcBadgeIn 800ms cubic-bezier(0.34, 1.56, 0.64, 1) both, bcBadgePulse 1600ms ease-in-out 900ms infinite",
          }}
        >
          <Activity size={30} color="#020202" strokeWidth={3} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
          <span style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
            fontSize: "clamp(2.2rem, 5vw, 3.2rem)",
            letterSpacing: "0.18em", textTransform: "uppercase",
            display: "inline-flex", whiteSpace: "pre",
          }}>
            <span ref={betRef} style={{ visibility: "hidden", display: "inline-flex" }}>BET</span>
            <span ref={crackerRef} style={{ visibility: "hidden", display: "inline-flex" }}> CRACKER</span>
          </span>
          <span style={{
            marginTop: "0.4rem",
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.62rem", letterSpacing: "0.4em",
            color: "rgba(255,255,255,0.35)", textTransform: "uppercase",
          }}>
            ANALISI CALCISTICA <span style={{
              display: "inline-block", width: 6, height: 10,
              background: cssAccent, marginLeft: 4, verticalAlign: -1,
              animation: "bcCaretBlink 1200ms ease-in-out infinite",
            }} />
          </span>
        </div>
      </div>
    </div>
  );
}
